(ns hai.web-app.error-boundary
  "Last-resort React error boundary + global error reporting.

   Without a boundary, React 18's `createRoot` unmounts the *entire* tree when
   any component throws during render, leaving a blank `<div id=\"app\">` — the
   \"white screen of death\". This is especially harmful for the installed
   mobile PWA (`display: standalone`), which has no URL bar or reload button, so
   a crash sticks until the user force-quits the app.

   [[error-boundary]] wraps the root render, converting any render-time throw
   into a recoverable \"Reload\" screen. [[install-global-error-handlers!]] wires
   up `window` error/rejection reporting so these crashes stop being invisible."
  (:require
   [lambdaisland.glogi :as log]
   [reagent.core :as reagent]))

(defn- ->component-stack
  "React's `errorInfo.componentStack` — the component tree at the throw site.
   The `^js` hint matters: `componentStack` is not in Closure's externs, so an
   untyped property access would be renamed away under `:advanced`."
  [^js info]
  (some-> info .-componentStack))

(defn report!
  "Log an unhandled error via glogi so it reaches the console/log sinks.
   `info` is React's error info for render errors, nil for window-level ones."
  [where error info]
  (log/error :web-app/unhandled-error
             {:where           where
              :message         (some-> error .-message)
              :stack           (some-> error .-stack)
              :component-stack (->component-stack info)}))

(def ^:private support-email "hello@humbleai.com")

(defn- error-report-body
  "Plain-text email body describing the crash. Both stacks go in whole: the
   component stack names the components that were rendering, which is what
   identifies a render bug. The minified JS stack often cannot — Reagent
   converts a component's hiccup *after* the component has returned, so an
   invalid form thrown there yields only library-internal frames."
  [error component-stack]
  (str "Please describe what you were doing when this happened:\n\n\n"
       "-------- technical details (please keep) --------\n"
       "Error: " (or (some-> error .-message) "Unknown error") "\n"
       "URL: " (or (some-> js/location .-href) "unknown") "\n"
       "Time: " (.toISOString (js/Date.)) "\n"
       "User agent: " (or (some-> js/navigator .-userAgent) "unknown") "\n\n"
       "Component stack:\n"
       (or component-stack "(none)")
       "\n\nStack:\n"
       (or (some-> error .-stack) "(no stack available)")))

(defn- mailto-href [error component-stack]
  (str "mailto:" support-email
       "?subject=" (js/encodeURIComponent "Humble app error report")
       "&body=" (js/encodeURIComponent (error-report-body error component-stack))))

(defn- fallback
  "Minimal, dependency-light crash screen. Deliberately uses inline styles and
   no component library / Ornament so it cannot itself throw."
  [error component-stack]
  [:div {:style {:min-height       "100vh"
                 :display          "flex"
                 :flex-direction   "column"
                 :align-items      "center"
                 :justify-content  "center"
                 :gap              "16px"
                 :padding          "24px"
                 :text-align       "center"
                 :box-sizing       "border-box"
                 :font-family      "system-ui, -apple-system, 'Segoe UI', sans-serif"
                 :color            "#1c1c1c"
                 :background        "#e4e2d2"}}
   [:div {:style {:font-size "18px" :font-weight "600"}}
    "Something went wrong"]
   [:div {:style {:font-size "14px" :max-width "320px" :color "#555" :line-height "1.5"}}
    "The app hit an unexpected error."]
   [:button {:on-click #(.reload js/location)
             :style    {:padding       "10px 20px"
                        :border        "none"
                        :border-radius "10px"
                        :background     "#4705D0"
                        :color          "#ffffff"
                        :font-size      "14px"
                        :font-weight    "600"
                        :cursor         "pointer"}}
    "Reload"]
   [:a {:href  (mailto-href error component-stack)
        :style {:font-size       "13px"
                :color           "#4705D0"
                :text-decoration "underline"
                :cursor          "pointer"}}
    "Email error to Humble"]])

(defn error-boundary
  "Form-2 boundary component. Use as `[error-boundary child-hiccup]`.

   `get-derived-state-from-error` runs in the render phase and flips the
   closure ratom *before* React re-renders, so the fallback is shown on the
   first error re-render (no throw/catch loop). `component-did-catch` runs in
   the commit phase and is used only for reporting."
  [_child]
  (let [!error           (reagent/atom nil)
        !component-stack (reagent/atom nil)]
    (reagent/create-class
     {:display-name "RootErrorBoundary"

      :get-derived-state-from-error
      (fn [error]
        (reset! !error error)
        #js {})

      :component-did-catch
      (fn [_this error info]
        ;; The component stack only exists here, in the commit phase — by which
        ;; point `get-derived-state-from-error` has already shown the fallback.
        ;; Recording it re-renders the fallback with the stack attached to its
        ;; report link, so it reaches us instead of dying on the device.
        (reset! !component-stack (->component-stack info))
        (report! :react/render error info))

      :reagent-render
      (fn [child]
        (if-let [error @!error]
          [fallback error @!component-stack]
          child))})))

(defonce ^:private !global-handlers-installed? (atom false))

(defn install-global-error-handlers!
  "Report uncaught errors and unhandled promise rejections that escape the
   React tree. Idempotent — safe to call on every (re)load."
  []
  (when-not @!global-handlers-installed?
    (reset! !global-handlers-installed? true)
    (.addEventListener js/window "error"
                       (fn [e] (report! :window/error (.-error e) nil)))
    (.addEventListener js/window "unhandledrejection"
                       (fn [e] (report! :window/unhandled-rejection (.-reason e) nil)))))
